import { BestRadio, DataTable, Icon } from "best-common-react";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
    getNeedsReviewRestoreContext,
    NeedsReviewMoveTarget,
    NeedsReviewRestoreContext,
    NeedsReviewRestoreRow,
} from "../../../api/RosterApi";
import { useAlert } from "../../../contexts/AlertsContext";
import { useGlobalModal } from "../../../contexts/GlobalModalContext";
import { DateUtil } from "../../../util/DateUtil";
import { OutlinedButtonStyled } from "../OutlinedButtonStyled";
import { PrimaryButtonStyled } from "../PrimaryButtonStyled";

export type NeedsReviewActionType = "" | "RESTORE" | "MOVE";

const ModalBody = styled.div.attrs(() => ({
    className: "m-4",
}))`
    min-height: 280px;
`;

const ActionRow = styled.div`
    display: flex;
    justify-content: flex-start;
    margin-bottom: 16px;
`;

const StyledSelect = styled.select`
    font-family: Helvetica;
    font-size: 13px;
    height: 28px;
    min-width: 220px;
    border: 1px solid var(--blue-grey);
    border-radius: 4px;
    padding: 0 8px;
    color: var(--slate);
`;

const MoveTargetSection = styled.div`
    margin-top: 8px;
`;

const MoveTargetList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const MoveTargetRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
`;

const MoveTargetOptionLabel = styled.label`
    display: block;
    font-family: Helvetica;
    font-size: 14px;
    color: var(--slate);
    margin-bottom: 0;
    cursor: pointer;
`;

const MoveTargetWarning = styled.div`
    font-family: Helvetica;
    font-size: 12px;
    color: var(--red);
    margin-top: 2px;
    line-height: 1.4;
`;

const InstructionHintBox = styled.div`
    width: 100%;
    padding: 12px 14px;
    margin-bottom: 12px;
    border: 1px solid var(--blue-grey);
    background-color: var(--cloud);
    border-radius: 4px;
    font-family: Helvetica;
    font-size: 13px;
    line-height: 1.5;
    color: var(--slate);
`;

const TriggerCommentsBox = styled(InstructionHintBox)`
    white-space: pre-wrap;
    max-height: 290px;
    overflow-y: auto;
`;

const ErrorText = styled.div`
    font-family: Helvetica;
    font-size: 14px;
    color: var(--red);
`;

const ReasonUnavailableText = styled(ErrorText)`
    margin-bottom: 16px;
    font-style: italic;
    color: var(--slate);
`;

const LoadingText = styled.div`
    font-family: Helvetica;
    font-size: 14px;
    color: var(--slate);
    font-style: italic;
`;

const StyledActivityTable = styled.div`
    .react-grid-Grid {
        border: 1px solid #e0e6ed;
        overflow: hidden;
    }

    .react-grid-Header,
    .react-grid-HeaderRow {
        overflow: hidden !important;
        -ms-overflow-style: none;
        scrollbar-width: none;
    }

    .react-grid-Header::-webkit-scrollbar,
    .react-grid-HeaderRow::-webkit-scrollbar {
        display: none;
        width: 0;
        height: 0;
    }

    .react-grid-Canvas {
        overflow-x: hidden !important;
        overflow-y: auto !important;
    }

    .react-grid-HeaderCell.active-selector-id,
    .react-grid-Cell.active-selector-id {
        text-align: center !important;
    }

    .react-grid-Cell.active-selector-id .react-grid-Cell__value {
        display: flex;
        justify-content: center;
        width: 100%;
    }
`;

const FilterRow = styled.div.attrs(() => ({
    className: "d-flex align-items-center justify-content-between mb-3",
}))``;

const StyledIcon = styled(Icon)<{ color: string }>`
    color: ${props => props.color};
`;

const ClickableIconWrapper = styled.div`
    cursor: pointer;
    display: flex;
    justify-content: center;
`;

const SelectionHint = styled.div`
    font-family: Helvetica;
    font-size: 13px;
    color: var(--red);
    text-align: right;
    margin-bottom: 8px;
`;

const INSTRUCTION_HINT_TEXT =
    'Below is the status the workflow was in right before being sent to needs review. Click the status icons to change which statuses stay active when restoring the workflow. Click "Resolve" to set the workflow to those statuses. Click "Reset" to restore the icon selections to what they were when the person entered Needs Review.';

type RestoreSelectionContextType = {
    selectedRosterStatusIds: number[];
    toggleRowSelection: (rosterStatusId: number) => void;
};

const RestoreSelectionContext = createContext<RestoreSelectionContextType>({
    selectedRosterStatusIds: [],
    toggleRowSelection: () => {},
});

type RowFormatterProps = {
    row: NeedsReviewRestoreRow;
};

const DateFormatter: React.FC<RowFormatterProps> = ({ row }) => (
    <div>{row.date ? DateUtil.formatDate(row.date, "MM/DD/YYYY - hh:mma") : ""}</div>
);

const StatusFormatter: React.FC<RowFormatterProps> = ({ row }) => <div>{row.status}</div>;

const DescriptionFormatter: React.FC<RowFormatterProps> = ({ row }) => (
    <div>{row.description || row.comment || ""}</div>
);

const UserFormatter: React.FC<RowFormatterProps> = ({ row }) => <div>{row.user}</div>;

const RestoreActiveFormatter: React.FC<RowFormatterProps> = ({ row }) => {
    const { selectedRosterStatusIds, toggleRowSelection } = useContext(RestoreSelectionContext);
    const isSelected =
        row.rosterStatusId != null && selectedRosterStatusIds.includes(row.rosterStatusId);

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (row.rosterStatusId != null) {
            toggleRowSelection(row.rosterStatusId);
        }
    };

    return (
        <div className="d-flex justify-content-center">
            <ClickableIconWrapper onClick={handleClick}>
                <StyledIcon
                    color={isSelected ? "green" : "red"}
                    iconName={isSelected ? "fa-check" : "fa-times"}
                />
            </ClickableIconWrapper>
        </div>
    );
};

const restorePreviewColumns = [
    {
        key: "date",
        name: "Date",
        width: 150,
        formatter: DateFormatter,
    },
    {
        key: "status",
        name: "Status",
        width: 150,
        formatter: StatusFormatter,
    },
    {
        key: "description",
        name: "Description",
        formatter: DescriptionFormatter,
    },
    {
        key: "user",
        name: "User",
        width: 120,
        formatter: UserFormatter,
    },
    {
        key: "active",
        name: "Active",
        width: 90,
        formatter: RestoreActiveFormatter,
    },
];

type MoveFromNeedsReviewBodyProps = {
    personnelInfo: PersonnelInfoType;
    actionType: NeedsReviewActionType;
    setActionType: React.Dispatch<React.SetStateAction<NeedsReviewActionType>>;
    targetStatus: string;
    setTargetStatus: React.Dispatch<React.SetStateAction<string>>;
    moveTargets: NeedsReviewMoveTarget[];
    setMoveTargets: React.Dispatch<React.SetStateAction<NeedsReviewMoveTarget[]>>;
    selectedRosterStatusIds: number[];
    setSelectedRosterStatusIds: React.Dispatch<React.SetStateAction<number[]>>;
    originalPriorRosterStatusIds: number[];
    setOriginalPriorRosterStatusIds: React.Dispatch<React.SetStateAction<number[]>>;
    showHidden: boolean;
    setShowHidden: React.Dispatch<React.SetStateAction<boolean>>;
};

type MoveFromNeedsReviewFooterProps = {
    personnelInfo: PersonnelInfoType;
    actionType: NeedsReviewActionType;
    targetStatus: string;
    moveTargets: NeedsReviewMoveTarget[];
    selectedRosterStatusIds: number[];
    resolveFromNeedsReviewCallback: (
        personnelId: number,
        resolution:
            | { action: "RESTORE"; rosterStatusIds: number[] }
            | { action: "MOVE_TO_STATUS"; targetStatus: string },
    ) => Promise<void>;
};

export type PersonnelInfoType = {
    id: number;
    lastName: string;
    firstName: string;
    middleName?: string;
    title: string;
    affiliation: {
        orgAbbreviation: string;
        name: string;
    };
};

export const formatMoveFromNeedsReviewHeader = (info: PersonnelInfoType): string => {
    const name = [info.firstName, info.middleName, info.lastName].filter(Boolean).join(" ");
    const abbr = info.affiliation?.orgAbbreviation ?? "";
    return `Move from Needs Review - ${name} | ${abbr}`;
};

const MoveFromNeedsReviewBody: React.FC<MoveFromNeedsReviewBodyProps> = ({
    personnelInfo,
    actionType,
    setActionType,
    targetStatus,
    setTargetStatus,
    moveTargets,
    setMoveTargets,
    selectedRosterStatusIds,
    setSelectedRosterStatusIds,
    originalPriorRosterStatusIds,
    setOriginalPriorRosterStatusIds,
    showHidden,
    setShowHidden,
}) => {
    const showAlert = useAlert();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [availableRows, setAvailableRows] = useState<NeedsReviewRestoreRow[]>([]);
    const [needsReviewComment, setNeedsReviewComment] = useState<string>("");

    useEffect(() => {
        setLoading(true);
        setError(null);
        getNeedsReviewRestoreContext(personnelInfo.id)
            .then((context: NeedsReviewRestoreContext) => {
                setAvailableRows(context.availableRows || []);
                const priors = context.priorRosterStatusIds || [];
                setOriginalPriorRosterStatusIds(priors);
                setSelectedRosterStatusIds(priors);
                setMoveTargets(context.moveTargets || []);
                setNeedsReviewComment(context.needsReviewComment || "");
                setLoading(false);
            })
            .catch(err => {
                console.error("Error fetching Needs Review restore context:", err);
                setError("Failed to load prior statuses. Please try again.");
                setLoading(false);
                showAlert("Error loading prior statuses, Please contact BIGS Systems.", "danger");
            });
    }, [personnelInfo.id]);

    const filteredRows = useMemo(() => {
        if (showHidden) {
            return availableRows;
        }
        return availableRows.filter(row => row.visible === "Y");
    }, [availableRows, showHidden]);

    const toggleRowSelection = useCallback(
        (rosterStatusId: number) => {
            setSelectedRosterStatusIds(prev =>
                prev.includes(rosterStatusId)
                    ? prev.filter(id => id !== rosterStatusId)
                    : [...prev, rosterStatusId],
            );
        },
        [setSelectedRosterStatusIds],
    );

    const handleReset = () => {
        setSelectedRosterStatusIds([...originalPriorRosterStatusIds]);
    };

    const selectionContextValue = useMemo(
        () => ({
            selectedRosterStatusIds,
            toggleRowSelection,
        }),
        [selectedRosterStatusIds, toggleRowSelection],
    );

    return (
        <ModalBody>
            {loading && <LoadingText>Loading...</LoadingText>}
            {error && <ErrorText>{error}</ErrorText>}

            {!loading && !error && (
                <>
                    {needsReviewComment.trim() ? (
                        <TriggerCommentsBox>{needsReviewComment}</TriggerCommentsBox>
                    ) : (
                        <ReasonUnavailableText>No comment recorded.</ReasonUnavailableText>
                    )}
                    <ActionRow>
                        <StyledSelect
                            aria-label="Resolution action"
                            value={actionType}
                            onChange={e => {
                                const value = e.target.value as NeedsReviewActionType;
                                setActionType(value);
                                if (value !== "MOVE") {
                                    setTargetStatus("");
                                }
                            }}
                        >
                            <option value="">Select action...</option>
                            <option value="RESTORE">Restore to prior statuses</option>
                            <option value="MOVE">Move to...</option>
                        </StyledSelect>
                    </ActionRow>

                    {actionType === "RESTORE" && (
                        <>
                            <InstructionHintBox>{INSTRUCTION_HINT_TEXT}</InstructionHintBox>
                            <FilterRow>
                                <label className="mb-0 d-flex align-items-center">
                                    <input
                                        type="checkbox"
                                        checked={showHidden}
                                        onChange={e => setShowHidden(e.target.checked)}
                                        className="mr-1"
                                    />
                                    Show hidden
                                </label>
                                <OutlinedButtonStyled width={80} height={26} onClick={handleReset}>
                                    Reset
                                </OutlinedButtonStyled>
                            </FilterRow>
                            <RestoreSelectionContext.Provider value={selectionContextValue}>
                                <StyledActivityTable>
                                    <DataTable
                                        columns={restorePreviewColumns}
                                        data={filteredRows}
                                        height={320}
                                        rowHeight={35}
                                        headerRowHeight={30}
                                    />
                                </StyledActivityTable>
                            </RestoreSelectionContext.Provider>
                        </>
                    )}

                    {actionType === "MOVE" && (
                        <MoveTargetSection>
                            <MoveTargetList role="radiogroup" aria-label="Target status">
                                {moveTargets.map(target => {
                                    const inputId = `needs-review-move-${target.status.replace(
                                        /\s+/g,
                                        "-",
                                    )}`;
                                    return (
                                        <MoveTargetRow key={target.status}>
                                            <BestRadio
                                                id={inputId}
                                                checked={targetStatus === target.status}
                                                disabled={!target.valid}
                                                onChange={() => setTargetStatus(target.status)}
                                            />
                                            <div>
                                                <MoveTargetOptionLabel htmlFor={inputId}>
                                                    {target.status}
                                                </MoveTargetOptionLabel>
                                                {!target.valid && target.errorMessage && (
                                                    <MoveTargetWarning>
                                                        {target.errorMessage}
                                                    </MoveTargetWarning>
                                                )}
                                            </div>
                                        </MoveTargetRow>
                                    );
                                })}
                            </MoveTargetList>
                        </MoveTargetSection>
                    )}
                </>
            )}
        </ModalBody>
    );
};

const MoveFromNeedsReviewFooter: React.FC<MoveFromNeedsReviewFooterProps> = ({
    personnelInfo,
    actionType,
    targetStatus,
    moveTargets,
    selectedRosterStatusIds,
    resolveFromNeedsReviewCallback,
}) => {
    const { closeModal } = useGlobalModal();
    const showAlert = useAlert();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const hasValidMoveSelection = useMemo(() => {
        if (!targetStatus) {
            return false;
        }
        const target = moveTargets.find(t => t.status === targetStatus);
        return Boolean(target?.valid);
    }, [moveTargets, targetStatus]);

    const canSubmit =
        actionType === "RESTORE"
            ? selectedRosterStatusIds.length > 0
            : actionType === "MOVE"
            ? hasValidMoveSelection
            : false;

    const showHint = !canSubmit;

    const hintText =
        actionType === "RESTORE"
            ? "Select at least one status to restore"
            : actionType === "MOVE"
            ? "Select a target status"
            : "Select an action to continue";

    const submitResolve = async () => {
        setIsSubmitting(true);
        try {
            if (actionType === "RESTORE") {
                await resolveFromNeedsReviewCallback(personnelInfo.id, {
                    action: "RESTORE",
                    rosterStatusIds: selectedRosterStatusIds,
                });
            } else if (actionType === "MOVE") {
                await resolveFromNeedsReviewCallback(personnelInfo.id, {
                    action: "MOVE_TO_STATUS",
                    targetStatus,
                });
            }
            closeModal();
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Failed to resolve workflow. Please try again.";
            showAlert(errorMessage, "danger");
            setIsSubmitting(false);
        }
    };

    return (
        <div className="w-100">
            {showHint && <SelectionHint>{hintText}</SelectionHint>}
            <div className="d-flex justify-content-end w-100">
                <div className="mr-2">
                    <PrimaryButtonStyled
                        width={100}
                        height={26}
                        disabled={isSubmitting || !canSubmit}
                        onClick={event => {
                            event.stopPropagation();
                            submitResolve();
                        }}
                    >
                        {isSubmitting ? "Resolving..." : "Resolve"}
                    </PrimaryButtonStyled>
                </div>

                <OutlinedButtonStyled
                    width={80}
                    height={26}
                    disabled={isSubmitting}
                    onClick={event => {
                        event.stopPropagation();
                        closeModal();
                    }}
                >
                    Cancel
                </OutlinedButtonStyled>
            </div>
        </div>
    );
};

MoveFromNeedsReviewBody.displayName = "BodyContent";
MoveFromNeedsReviewFooter.displayName = "FooterContent";

export { MoveFromNeedsReviewBody, MoveFromNeedsReviewFooter };
